import React, { useCallback, useEffect } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { UpdateWorkItemAllFieldTypesAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';
import { getActionData } from '../../../utils';

import type { priorityField_updateWorkItemAllFieldTypesActionRenderer$key } from './__generated__/priorityField_updateWorkItemAllFieldTypesActionRenderer.graphql';

const messages = defineMessages({
	action: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.priority-field.action',
		defaultMessage: 'Set priority to {value}',
		description: 'Label for the change action in the priority field',
	},
	setPriority: {
		id: 'update-work-item-all-fields-action-renderer.ui.action-card.priority-field.setPriority',
		defaultMessage: 'Set priority from {originalValue} to {value}',
		description: 'Label for the set priority in the priority field',
	},
});

export type Props = {
	queryRef: priorityField_updateWorkItemAllFieldTypesActionRenderer$key;
	action: UpdateWorkItemAllFieldTypesAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const PriorityField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	di(useAnalytics);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const { fieldKey, fieldValue, fieldReasoning } = getActionData(action);

	const data = useFragment(
		graphql`
			fragment priorityField_updateWorkItemAllFieldTypesActionRenderer on JiraPriorityField {
				fieldId
				priority {
					name
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = Boolean(data?.fieldId) && data.fieldId === fieldKey;

	const value = String(fieldValue ?? '').trim();
	const formattedMessage = data?.priority?.name
		? formatMessage(messages.setPriority, { originalValue: data.priority.name, value })
		: formatMessage(messages.action, { value });

	const isSameValueSuggested =
		isFieldRenderable &&
		action.status === 'planned' &&
		Boolean(data?.priority?.name) &&
		data?.priority?.name === value;

	useEffect(() => {
		if (isSameValueSuggested && fg('jira-track-invalid-update-workitem-actions')) {
			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'detected',
				actionSubject: 'invalidField',
				actionSubjectId: 'updateWorkActionRenderer',
				attributes: {
					fieldType: 'priority',
					anomaly: 'sameValueSuggested',
					invocationId: action.invocationId,
				},
			});
		}
	}, [isSameValueSuggested, action.invocationId, sendAnalyticsEvent]);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={fieldReasoning}
				additionalSuggestions={[
					{
						suggested: formattedMessage,
						testIdSuffix: 'priority-suggested-update',
					},
				]}
			/>
		),
		[fieldReasoning, formattedMessage],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: fieldKey,
		provider: toolTipContentProvider,
		enabled:
			isFieldRenderable &&
			FeatureGates.getExperimentValue(
				'jira-ai-update-workitem-field-action-details-exp',
				'isEnabled',
				false,
			),
	});

	if (!isFieldRenderable) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-all-fields-action-renderer.ui.action-card.card-fields-list.priority-field"
		>
			{formattedMessage}
		</Text>
	);
};
